/**
 * The Historian variable tree: Site → Category → Device → Metric
 * (djin-cloud#806; categories #954).
 *
 * Pure and dependency-free on purpose. Every interesting rule on this page —
 * what a search keeps, what auto-expands, which sites are still loading — is
 * decided here, so it can be tested without jsdom, a provider or a catalog
 * fetch. `VariableBrowser.tsx` is left with rendering.
 *
 * ⚠️ The three search rules are ASYMMETRIC and the asymmetry is the feature:
 *   - a SITE match keeps every device and every metric (you asked for the
 *     site, not for a filtered view of it);
 *   - a DEVICE match keeps every metric of that device (same reasoning, one
 *     level down);
 *   - a METRIC match keeps ONLY the matching metrics, because that is the
 *     only level where the query names the leaf you want.
 * Collapsing any two of those into one predicate reads like a simplification
 * and silently changes what the operator sees.
 */

import type { HistorianCatalog } from '@/services/historianApi';

export interface MetricNode {
  kind: 'metric';
  key: string;
  siteId: string;
  deviceId: string;
  name: string;
  unit: string | null;
  lastSeen: string;
}

export interface DeviceNode {
  kind: 'device';
  key: string;
  siteId: string;
  deviceId: string;
  label: string;
  deviceType: string;
  blockId: string | null;
  /**
   * Roster rows behind this node (see `CatalogDevice.rosterDeviceIds`). Empty
   * for telemetry no roster row claims — and for a catalog from an API that
   * predates the field, which is why the browser's marker reads `unrostered`
   * rather than this array's length.
   */
  rosterDeviceIds: string[];
  /** See `deviceSubtitle`. */
  subtitle: string;
  /**
   * Telemetry that no roster row claims, other than the site summary (which is
   * not a device). Always false when the catalog came from an API that predates
   * `rosterDeviceIds`: an absent field is not evidence of absence, and flagging
   * every device of a mid-rollout page "not in roster" would be a false claim.
   */
  unrostered: boolean;
  /**
   * Whether the node has reported any metric in the catalog's
   * lookback window. The catalog deliberately returns devices with an empty
   * `metrics` array rather than omitting them, so the tree can grey them —
   * a device that vanishes from the browser reads as "not installed", which
   * is a different and worse claim than "no data in 30 d".
   */
  hasData: boolean;
  metrics: MetricNode[];
}

/**
 * Device categories, in display order (djin-cloud#954). The site summary is not
 * a category — it is not a device — and renders above the groups on its own.
 */
export type DeviceCategory = 'battery' | 'generator' | 'solar' | 'meter' | 'ev' | 'other';

export const CATEGORY_ORDER: readonly DeviceCategory[] = ['battery', 'generator', 'solar', 'meter', 'ev', 'other'];

export const CATEGORY_LABELS: Record<DeviceCategory, string> = {
  battery: 'Battery storage',
  generator: 'Generators',
  solar: 'Solar',
  meter: 'Meters',
  ev: 'EV chargers',
  other: 'Other',
};

/**
 * Roster `device_type` → category, keyed on EXACTLY the roster vocabulary:
 * `config.site_devices.device_type` is CHECK-constrained by migration 026 to
 * `generator, battery_storage, pcs, meter, solar_pv, ev_charger, other`.
 * Matched case-insensitively. Anything else — the catalog's own `site` and
 * `unknown`, or a value a later migration adds — lands in `other` rather than
 * disappearing: a device missing from the browser reads as "not installed".
 *
 * ⚠️ Extend this from the constraint, never from what a type "is probably
 * called". The first version guessed `pv`/`solar`/`pv_inverter` and missed
 * `solar_pv`, so every real solar device fell into Other (PR#955 review).
 * `historianTree.test.ts` pins every constraint value.
 */
const TYPE_CATEGORY: Record<string, DeviceCategory> = {
  battery_storage: 'battery',
  pcs: 'battery',
  generator: 'generator',
  solar_pv: 'solar',
  meter: 'meter',
  ev_charger: 'ev',
  other: 'other',
};

export function deviceCategory(deviceType: string): DeviceCategory {
  return TYPE_CATEGORY[deviceType.toLowerCase()] ?? 'other';
}

/** The catalog's type for the site-level KPI node (`historian-catalog.service.ts`). */
export const SITE_SUMMARY_TYPE = 'site';

export interface DeviceGroupNode {
  kind: 'group';
  key: string;
  siteId: string;
  category: DeviceCategory;
  label: string;
  devices: DeviceNode[];
}

export interface SiteNode {
  kind: 'site';
  key: string;
  siteId: string;
  siteName: string;
  siteCode: string;
  status: 'unloaded' | 'loading' | 'loaded' | 'error';
  error?: string;
  /** Every device, flat and in catalog order — `summary` and `groups` partition it. */
  devices: DeviceNode[];
  /** The site-summary node(s), rendered above the category groups. */
  summary: DeviceNode[];
  /** Non-empty categories in `CATEGORY_ORDER`; devices keep catalog order within one. */
  groups: DeviceGroupNode[];
}

export interface SiteSummary {
  siteId: string;
  siteName: string;
  siteCode: string;
}

export interface BuildInput {
  sites: SiteSummary[];
  catalogs: Map<string, HistorianCatalog>;
  loading: Set<string>;
  errors: Map<string, string>;
  search: string;
}

export interface SiteTree {
  tree: SiteNode[];
  /**
   * Keys to render as open REGARDLESS of the user's own expansion state,
   * because a search that matched a metric three levels down is useless if
   * the path to it is collapsed. Empty when the search is empty — the user's
   * own expansion state is then the only input.
   *
   * A MEMBERSHIP set: the only consumer reads it through `.has()`. Iteration
   * order is therefore an observable with no consumer, and nothing — test or
   * caller — should grow a dependency on it.
   *
   * Holds only keys with something to REVEAL. A node that auto-opens onto
   * nothing (a name-matched site whose catalog was never fetched, an errored
   * site, a device with no metrics) renders identically to one that genuinely
   * has no data, which is a different and wrong claim.
   */
  autoExpand: Set<string>;
}

export const siteKey = (siteId: string) => `site:${siteId}`;
export const deviceKey = (siteId: string, deviceId: string) =>
  `${siteKey(siteId)}:device:${deviceId}`;
export const groupKey = (siteId: string, category: DeviceCategory) =>
  `${siteKey(siteId)}:group:${category}`;
export const metricKey = (siteId: string, deviceId: string, name: string) =>
  `${deviceKey(siteId, deviceId)}:metric:${name}`;

/** `needle` is expected pre-lowercased and pre-trimmed by the caller. */
const has = (hay: string | null | undefined, needle: string) =>
  !!hay && hay.toLowerCase().includes(needle);

/**
 * The muted text after a device's type badge: the ids that identify the node
 * but are not already its label — its block id, then the roster devices folded
 * into it (`BMS-001 + PCS-001` under block `ES-001`, #828). Returns '' when
 * every id is the label itself, so a meter whose block id equals its own id
 * does not print its name twice.
 */
export function deviceSubtitle(d: Pick<DeviceNode, 'label' | 'blockId' | 'rosterDeviceIds'>): string {
  const parts: string[] = [];
  if (d.blockId && d.blockId !== d.label) parts.push(d.blockId);
  const roster = d.rosterDeviceIds.filter((id) => id !== d.label && id !== d.blockId);
  if (roster.length > 0) parts.push(roster.join(' + '));
  return parts.join(' · ');
}

export function buildSiteTree({
  sites,
  catalogs,
  loading,
  errors,
  search,
}: BuildInput): SiteTree {
  const q = search.trim().toLowerCase();
  const autoExpand = new Set<string>();
  const tree: SiteNode[] = [];

  for (const site of sites) {
    const catalog = catalogs.get(site.siteId);
    const status: SiteNode['status'] = errors.has(site.siteId)
      ? 'error'
      : loading.has(site.siteId)
        ? 'loading'
        : catalog
          ? 'loaded'
          : 'unloaded';

    // An empty query matches everything, which is what makes the rest of this
    // loop a single code path rather than a filtered and an unfiltered one.
    const siteMatch = !q || has(site.siteName, q) || has(site.siteCode, q);

    const devices: DeviceNode[] = [];
    for (const d of catalog?.devices ?? []) {
      const label = d.displayName ?? d.deviceId;
      const rosterDeviceIds = d.rosterDeviceIds ?? [];
      // A folded node must still be findable by the roster ids an operator
      // knows it by — `BMS-001` finds block `ES-001` (#828).
      const deviceMatch =
        siteMatch ||
        has(label, q) ||
        has(d.deviceId, q) ||
        has(d.blockId, q) ||
        rosterDeviceIds.some((id) => has(id, q));

      const metrics: MetricNode[] = d.metrics
        .filter((m) => deviceMatch || has(m.name, q))
        .map((m) => ({
          kind: 'metric',
          key: metricKey(site.siteId, d.deviceId, m.name),
          siteId: site.siteId,
          deviceId: d.deviceId,
          name: m.name,
          unit: m.unit,
          lastSeen: m.lastSeen,
        }));

      // A device with no metrics is kept when the device itself matched —
      // that is the only way a data-less device (BESS on its blockId) is ever
      // reachable, since it has no metric text to match on.
      if (!deviceMatch && metrics.length === 0) continue;

      devices.push({
        kind: 'device',
        key: deviceKey(site.siteId, d.deviceId),
        siteId: site.siteId,
        deviceId: d.deviceId,
        label,
        deviceType: d.deviceType,
        blockId: d.blockId,
        rosterDeviceIds,
        subtitle: deviceSubtitle({ label, blockId: d.blockId, rosterDeviceIds }),
        unrostered:
          d.rosterDeviceIds !== undefined && d.rosterDeviceIds.length === 0 && d.deviceType !== 'site',
        hasData: d.metrics.length > 0,
        metrics,
      });
    }

    // `status !== 'error'` is the exception that keeps a FAILURE visible. A
    // filtered-out error row takes its retry with it, and the site then reads
    // as absent rather than as broken — the operator has no way to tell that
    // the catalog request failed, or to ask again.
    if (q && !siteMatch && devices.length === 0 && status !== 'error') continue;

    const summary = devices.filter((d) => d.deviceType === SITE_SUMMARY_TYPE);
    const groups: DeviceGroupNode[] = CATEGORY_ORDER.map((category) => ({
      kind: 'group' as const,
      key: groupKey(site.siteId, category),
      siteId: site.siteId,
      category,
      label: CATEGORY_LABELS[category],
      devices: devices.filter((d) => d.deviceType !== SITE_SUMMARY_TYPE && deviceCategory(d.deviceType) === category),
    })).filter((g) => g.devices.length > 0);

    tree.push({
      kind: 'site',
      key: siteKey(site.siteId),
      siteId: site.siteId,
      siteName: site.siteName,
      siteCode: site.siteCode,
      status,
      error: errors.get(site.siteId),
      devices,
      summary,
      groups,
    });

    // Populated only AFTER the site is known to survive, and only for nodes
    // that have something to reveal — see `SiteTree.autoExpand`. An unloaded,
    // errored or empty node auto-opening onto zero children is indistinguishable
    // from one that reported no data.
    if (q && devices.length > 0) {
      autoExpand.add(siteKey(site.siteId));
      // Every surviving group has at least one surviving device, and a surviving
      // device is itself a match to reveal — even a data-less one (see above).
      for (const g of groups) autoExpand.add(g.key);
      for (const d of devices) {
        if (d.metrics.length > 0) autoExpand.add(d.key);
      }
    }
  }

  return { tree, autoExpand };
}
